/**
 * Finding the addresses inside a message, so they can be tapped.
 *
 * A message is plain text and stays plain text — nothing is stored about a link
 * and nothing is rewritten on the way in. Like the smiley codes, this is a parse
 * on the way out, which means an address in an old message becomes clickable the
 * moment this ships, and a message quoted, pushed or listed elsewhere is still
 * the same string.
 *
 * Only two shapes are recognised: something with an explicit `http(s)://`, and
 * something that begins `www.`. A bare `example.com` is deliberately not one of
 * them — «файл.txt», «см. п.2» and every abbreviation with a dot in it would
 * become links, and turning ordinary Russian text blue is a worse failure than
 * making somebody type four extra characters.
 */
export type TextRun = { type: 'text'; value: string };
export type LinkRun = { type: 'link'; href: string; value: string };

/**
 * `<>«»"'` are excluded from the match rather than trimmed afterwards: they are
 * how an address gets wrapped in a sentence, and unlike a full stop they can
 * never be part of one.
 */
const ADDRESS = /(?:https?:\/\/|www\.)[^\s<>«»"']+/gi;

/** Punctuation that ends the sentence rather than the address. */
const TRAILING = new Set(['.', ',', ';', ':', '!', '?', '…', ')', ']', '}', '»']);

const CLOSERS: Record<string, string> = { ')': '(', ']': '[', '}': '{' };

/**
 * Drops the punctuation a writer put after the address.
 *
 * A closing bracket is only punctuation if nothing inside the address opened it
 * — otherwise `…/wiki/Кот_(значения)` loses its last character, which is exactly
 * the kind of link somebody pastes.
 */
function trimTrailing(raw: string): string {
  let end = raw.length;
  while (end > 0) {
    const last = raw[end - 1]!;
    if (!TRAILING.has(last)) break;
    const opener = CLOSERS[last];
    if (opener) {
      const inside = raw.slice(0, end);
      const opened = inside.split(opener).length - 1;
      const closed = inside.split(last).length - 1;
      if (opened >= closed) break;
    }
    end -= 1;
  }
  return raw.slice(0, end);
}

/**
 * The address to actually navigate to, or null when the match was not one.
 *
 * `new URL` is the check as well as the conversion: it rejects the half-typed
 * `http://` and the lone `www.`, and it normalises the scheme and the host —
 * which is what makes «Www.example.com» work, the shape an iPhone produces when
 * the address is the first thing typed in the field and iOS capitalises it.
 */
export function hrefFor(raw: string): string | null {
  const absolute = /^www\./i.test(raw) ? `https://${raw}` : raw;
  try {
    const url = new URL(absolute);
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return null;
    if (!url.hostname.includes('.')) return null;
    return url.href;
  } catch {
    return null;
  }
}

/** Splits a message body into the runs of ordinary text and the addresses in it. */
export function splitLinks(body: string): (TextRun | LinkRun)[] {
  const runs: (TextRun | LinkRun)[] = [];
  let at = 0;

  for (const match of body.matchAll(ADDRESS)) {
    const raw = trimTrailing(match[0]);
    const href = hrefFor(raw);
    if (!href) continue;
    if (match.index > at) runs.push({ type: 'text', value: body.slice(at, match.index) });
    runs.push({ type: 'link', href, value: raw });
    at = match.index + raw.length;
  }

  if (at < body.length) runs.push({ type: 'text', value: body.slice(at) });
  return runs;
}
